import { Box } from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';

import { CustomerUserDetails, ReportInstance } from '../../../backendsdk';
import { DEVICE_FILTER_OPTIONS, getExtraFilters } from './ReportDetails';
import { ASSIGNEE_OPTIONS, AlertReport, REVERSED_SEVERITY_FILTER_VALUES } from './ReportOptions';
import { ReportRuleFilterChips } from './ReportRuleFilterChips';

React;

export interface ReportFilterDisplayDeps {
    t: TFunction;
    profileCustomerName: string;
    geofences: Record<string, string>;
    users?: CustomerUserDetails[];
}

export const createReportFilterDisplay = (deps: ReportFilterDisplayDeps) => {
    const { t, profileCustomerName, geofences, users } = deps;

    const getGroupedFilters = (tKey: string, key: string, values: Array<string>) => {
        const formattedKey = t(`content.reports.form.${tKey}.${key.toLocaleLowerCase()}`, key);
        if (values.length === 0) {
            return { key: formattedKey };
        } else if (values.length > 1) {
            return { key: `${values.length} ${t(`content.reports.form.${tKey}.${key.toLocaleLowerCase()}_plural`)}` };
        } else {
            return {
                key: formattedKey,
                value: t(`content.reports.form.filter.${values[0].toLocaleLowerCase()}`, values[0]),
            };
        }
    };

    const getUngroupedFilters = (tKey: string, key: string, values: Array<string> | string) => {
        let formattedValues: string[] | undefined;
        if (key != DEVICE_FILTER_OPTIONS.ALL) {
            if (key.toLocaleLowerCase() === 'assignee' && values.includes(ASSIGNEE_OPTIONS.CUSTOMER)) {
                formattedValues = [profileCustomerName];
            } else if (key.toLocaleLowerCase() === 'severity' && typeof values === 'string') {
                if (values in REVERSED_SEVERITY_FILTER_VALUES) {
                    const valueName =
                        REVERSED_SEVERITY_FILTER_VALUES[values as keyof typeof REVERSED_SEVERITY_FILTER_VALUES];
                    formattedValues = [t(`content.reports.form.filter.${valueName.toLocaleLowerCase()}`)];
                } else {
                    formattedValues = [values];
                }
            } else if (key.toLocaleLowerCase() === 'geofence' && values.length > 0) {
                formattedValues = (values as string[]).map((s: string) => geofences[s]);
            } else if (
                key.toLocaleLowerCase() === 'requesting_user' &&
                Array.isArray(values) &&
                (values as string[]).length > 0
            ) {
                const userById = Object.fromEntries((users ?? []).map((u) => [String(u.user_id), u.name]));
                formattedValues = (values as string[]).map((id) => userById[id] ?? id);
            } else if (key === 'SESSION_STATUS' && Array.isArray(values)) {
                formattedValues = (values as string[]).map((s) =>
                    t(`content.reports.form.filter.session_status_${s.toLocaleLowerCase()}`, s),
                );
            } else {
                formattedValues = (values as string[]).map((s: string) =>
                    t(`content.reports.form.filter.${s.toLocaleLowerCase()}`, s),
                );
            }
        }
        return {
            key: t(`content.reports.form.${tKey}.${key.toLocaleLowerCase()}`, key),
            values: formattedValues,
        };
    };

    const formatFilters = (tKey: string, key: string, values: Array<string> | string, groupValues = false) => {
        if (groupValues) {
            const res = getGroupedFilters(tKey, key, values as string[]);
            return res.value ? `${res.key}: ${res.value}` : res.key;
        }
        const res = getUngroupedFilters(tKey, key, values);
        return res.values ? `${res.key}: ${res.values.join(', ')}` : res.key;
    };

    const renderFilters = (tKey: string, key: string, values: Array<string> | string, groupValues = false) => {
        if (groupValues) {
            const res = getGroupedFilters(tKey, key, values as string[]);
            return (
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{res.key}</span>
                    {res.value && <span>: {res.value}</span>}
                </div>
            );
        }
        const res = getUngroupedFilters(tKey, key, values);
        return (
            <Box key={key} sx={{ display: 'flex' }}>
                <span style={{ fontWeight: 'bold', paddingTop: '4px', whiteSpace: 'nowrap' }}>{res.key}</span>
                {res.values && (
                    <>
                        <span style={{ paddingTop: '4px', marginRight: '4px' }}>:</span>
                        <ReportRuleFilterChips filterKey={key} values={res.values} />
                    </>
                )}
            </Box>
        );
    };

    const formatExtraFilters = (row: AlertReport | ReportInstance) => {
        const filters = getExtraFilters(row);
        return filters.map(([key, values]) => formatFilters('extra_filters', key, values)).join('; ');
    };

    const renderExtraFilters = (row: AlertReport | ReportInstance) => {
        const filters = getExtraFilters(row);
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {filters.map(([key, values]) => renderFilters('extra_filters', key, values))}
            </Box>
        );
    };

    return {
        getGroupedFilters,
        getUngroupedFilters,
        formatFilters,
        renderFilters,
        formatExtraFilters,
        renderExtraFilters,
    };
};
